import type { EUROPACE_PROCESS_PLATFORM } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuBan, LuRefreshCw } from 'react-icons/lu';

import { cn } from '~/utils/cnTailwind';

type Props = {
    currentRequestId?: string;
    availableCredit: number;
    possibilityCheckRunning: boolean;
    europacePlatformPartnerId: string | null;
    platform: EUROPACE_PROCESS_PLATFORM | undefined;
    requestOffersPermissionDenied: boolean;
    hasUnsavedChanges: boolean;
};

export const ButtonContent = ({
    availableCredit,
    currentRequestId,
    europacePlatformPartnerId,
    hasUnsavedChanges,
    platform,
    possibilityCheckRunning,
    requestOffersPermissionDenied,
}: Props) => {
    const { t } = useTranslation();

    const userNotAllowedContent = (
        <>
            <LuBan data-testid="decline-icon" className="size-7 shrink-0" />
            <div className="flex flex-col gap-1 items-center">
                <span className="font-medium">{t('BANK_CREDIT_REQUEST.USER_NOT_ALLOWED')}</span>
                <span className="text-xs text-balance italic">
                    {t('BANK_CREDIT_REQUEST.USER_NOT_ALLOWED_DESCRIPTION')}
                </span>
            </div>
        </>
    );
    const unsavedChangesContent = (
        <>
            <LuBan data-testid="decline-icon" className="size-7 shrink-0" />
            <div className="flex flex-col gap-1 items-center">
                <span className="font-medium">{t('BANK_CREDIT_REQUEST.UNSAVED_CHANGES')}</span>
                <span className="text-xs text-balance italic">
                    {t('BANK_CREDIT_REQUEST.UNSAVED_CHANGES_DESCRIPTION')}
                </span>
            </div>
        </>
    );
    const noFundsContent = (
        <>
            <LuBan data-testid="decline-icon" className="size-7 shrink-0" />
            <div className="flex flex-col gap-1 items-center">
                <span className="font-medium">{t('BANK_CREDIT_REQUEST.NO_SUFFICIENT_FUNDS')}</span>
                <span className="text-xs text-balance italic">
                    {t('BANK_CREDIT_REQUEST.NO_SUFFICIENT_FUNDS_DESCRIPTION')}
                </span>
            </div>
        </>
    );

    const newRequestContent = (
        <>
            <LuRefreshCw
                data-testid="refresh-icon"
                className={cn('size-6 shrink-0', {
                    'animate-spin': possibilityCheckRunning,
                })}
            />
            <div className="flex flex-col gap-1 items-center">
                <span className="font-medium">{t('BANK_CREDIT_REQUEST.START_INITIAL_CHECK')}</span>
                <span className="text-xs text-balance italic">{t('BANK_CREDIT_REQUEST.INITIAL_CHECK_COMMENT')}</span>
            </div>
        </>
    );

    const updateRequestContent = (
        <>
            <LuRefreshCw
                data-testid="refresh-icon"
                className={cn('size-6 shrink-0', {
                    'animate-spin': possibilityCheckRunning,
                })}
            />
            <span className="font-medium py-2.5">{t('BANK_CREDIT_REQUEST.UPDATE_INITIAL_CHECK')}</span>
        </>
    );

    // NOTE: the returns here need to be in order since they can overlap but we only show one of these elements
    if (requestOffersPermissionDenied) {
        return userNotAllowedContent;
    }

    if (europacePlatformPartnerId === null) {
        const currentPlatform = platform ?? 'GENERAL';
        const missingPartnerIdContent = (
            <>
                <LuBan data-testid="decline-icon" className="size-7 shrink-0" />
                <div className="flex flex-col gap-1 items-center">
                    <span className="font-medium">
                        {t(`BANK_CREDIT_REQUEST.MISSING_PARTNER_ID.${currentPlatform}`)}
                    </span>
                    <span className="text-xs text-balance italic">
                        {t(`BANK_CREDIT_REQUEST.MISSING_PARTNER_ID_DESCRIPTION.${currentPlatform}`)}
                    </span>
                </div>
            </>
        );
        return missingPartnerIdContent;
    }

    if (hasUnsavedChanges) {
        return unsavedChangesContent;
    }

    if (availableCredit < 0) {
        return noFundsContent;
    }
    if (currentRequestId) {
        return updateRequestContent;
    }
    return newRequestContent;
};
